import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cabinet } from '../../../lib/modelBuilder/entities/Cabinet';
import {
  CabinetSupport,
  PaneMaterial,
} from '../../../lib/modelBuilder/constants';
import {
  StyledTabularContainer,
  StyledTabularDataRow,
  StyledTabularTitle,
  StyledTabularValue,
} from '../../../components/styles/StyledTabular';
import ShelfDescription from './ShelfDescription';
import { Shelf } from '../../../lib/modelBuilder/entities/Shelf';
import PlateDescription from './ShelfDescription/PlateDescription';
import i18n from '../../../i18n';
import { FiMinus, FiPlus } from 'react-icons/all';

interface Props {
  cabinet: Cabinet;
}

const CabinetDescription = ({ cabinet }: Props) => {
  const { t } = useTranslation();

  const [isShelvesVisible, setVisibleShelves] = useState(false);

  const materialName = (material: PaneMaterial): string => {
    switch (material) {
      case PaneMaterial.MDF:
        return t('Mdf');
      case PaneMaterial.Frost:
        return t('short.acrylic');
      case PaneMaterial.FrostOpaque:
        return t('short.acrylic');
      default:
        return '';
    }
  };

  const getCabinetSupportTitle = () => {
    switch (cabinet.support) {
      case CabinetSupport.NoSupport:
        return t('No feets');
      case CabinetSupport.Feet:
        return `${t('Classic feets')} 10 cm`;
      case CabinetSupport.AdjustableFeet:
        return `${t('Adjustable feets')} ${`11cm ${t('to')} 12.3cm`}`;
      case CabinetSupport.WheelWhite:
        return `${t('Wheels')} ${t('with white plate')}`;
      case CabinetSupport.WheelBlack:
        return `${t('Wheels')} ${t('with black plate')}`;
      case CabinetSupport.ToHangScrew:
        return t('Suspension');
      case CabinetSupport.ToHangHook:
        return `${t('Suspension')} ${t('with hooks')}`;
    }
  };

  let languageCode = 'fr';
  if (i18n.language) {
    languageCode = i18n.language;
  }

  return (
    <>
      <div className="text-base py-3 pl-1">
        {cabinet.getDescription(languageCode)}
      </div>
      <StyledTabularContainer>
        <StyledTabularDataRow>
          <StyledTabularTitle>{t('Weight')}</StyledTabularTitle>
          <StyledTabularValue>
            {cabinet.weightKilo.toFixed(2)} kg
          </StyledTabularValue>
        </StyledTabularDataRow>
        <StyledTabularDataRow>
          <StyledTabularTitle>{t('Height')}</StyledTabularTitle>
          <StyledTabularValue className="font-semibold w-2/3">
            {cabinet.height / 10} cm
          </StyledTabularValue>
        </StyledTabularDataRow>
        <StyledTabularDataRow>
          <StyledTabularTitle>{t('Width')}</StyledTabularTitle>
          <StyledTabularValue>{cabinet.width / 10} cm</StyledTabularValue>
        </StyledTabularDataRow>
        <StyledTabularDataRow>
          <StyledTabularTitle>{t('Depth')}</StyledTabularTitle>
          <StyledTabularValue>{cabinet.depth / 10} cm</StyledTabularValue>
        </StyledTabularDataRow>
        <StyledTabularDataRow>
          <StyledTabularTitle>{t('Corners')}</StyledTabularTitle>
          <StyledTabularValue>
            {cabinet.corners[0].base.color.name}
          </StyledTabularValue>
        </StyledTabularDataRow>
        <PlateDescription
          title={t('Plate superior')}
          rack={cabinet.top}
          materialName={materialName}
        />
        <StyledTabularDataRow>
          <StyledTabularTitle>{t('Support')}</StyledTabularTitle>
          <StyledTabularValue>{getCabinetSupportTitle()}</StyledTabularValue>
        </StyledTabularDataRow>
      </StyledTabularContainer>

      <div>
        <button
          onClick={() => {
            setVisibleShelves(!isShelvesVisible);
          }}
          className="flex justify-center items-center w-full mt-6"
        >
          <div className="text-2xl">
            {t('Display each compartment in detail')}
          </div>
          <div className="ml-4">
            {isShelvesVisible ? (
              <FiMinus className="h-6 w-6" />
            ) : (
              <FiPlus className="h-6 w-6" />
            )}
          </div>
        </button>
        {isShelvesVisible &&
          cabinet.shelves
            .sort((a: Shelf, b: Shelf) => b.level - a.level)
            .map((shelf: Shelf) => (
              <ShelfDescription
                key={shelf.level}
                shelf={shelf}
                materialName={materialName}
              />
            ))}
      </div>
    </>
  );
};

export default CabinetDescription;
